import {
	ButtonBase,
	Grid,
	Paper,
	styled,
	Table,
	TableBody,
	TableCell,
	tableCellClasses,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import { useMainCustomStyle } from '../../assets/theme/Theme';
import SuperAdminSideNavigationBar from '../../components/SuperAdminSideNavigationBar';
import { FeedbackTopicIcon, MetricIcon } from '../../assets/icons/Icons';
import FeedbackCount from '../../assets/images/FeedbackCount.gif';
import Accuracy from '../../assets/images/Metric.gif';
import ThumpDown from '../../assets/images/ThumbsDown.gif';
import ThumpUp from '../../assets/images/ThumbsUp.gif';
import { MAIN_THEME_COLOR, WHITE } from '../../assets/theme/Colors';
import { useEffect, useState } from 'react';
import {
	fetchAllFeedbacks,
	fetchFeedbackMetrics,
} from '../../services/FeedbackService';
import { getAccessToken } from '../../Utils/AuthUtil';

const FILTER_TAGS = ['All', 'Positive', 'Negative'];

function FilterTag({ name, isSelected, onSelect }) {
	return (
		<ButtonBase
			onClick={() => onSelect(name)}
			sx={{
				backgroundColor: isSelected ? MAIN_THEME_COLOR : '#F9F8F9',
				borderRadius: '22px',
				padding: '2px 10px 2px 10px',
			}}>
			<Typography
				fontSize={'14px'}
				color={isSelected ? WHITE : '#686868'}>
				{name}
			</Typography>
		</ButtonBase>
	);
}

function TextWithTooltip({ text }) {
	if (text.length < 30) {
		return <Typography variant="tableBody">{text}</Typography>;
	} else {
		return (
			<Tooltip title={text} placement="right" arrow>
				<Typography variant="tableBody" noWrap>
					{text.slice(0, 30) + '...'}
				</Typography>
			</Tooltip>
		);
	}
}

function MetricTile({ count, name, image }) {
	return (
		<Grid
			item
			xs={12}
			md={3}
			sx={{
				backgroundColor: '#F4F4F8',
				borderRadius: '6px',
				flexDirection: 'row',
				alignItems: 'center',
				display: 'flex',
				padding: '24px',
			}}>
			<Grid
				sx={{
					width: '50px',
					height: '50px',
					backgroundColor: '#FFFFFF',
					justifyContent: 'center',
					alignItems: 'center',
					display: 'flex',
					padding: '10px',
					borderRadius: '100px',
				}}>
				<img src={image} width={'40px'} height={'40px'} />
			</Grid>
			<Grid sx={{ flexDirection: 'column', marginLeft: '15px' }}>
				<Typography
					color={'#324054'}
					fontSize={'18px'}
					fontWeight={'500'}>
					{count}
				</Typography>
				<Typography
					color={'#324054'}
					fontSize={'14px'}
					fontWeight={'500'}>
					{name}
				</Typography>
			</Grid>
		</Grid>
	);
}

function FeedbackDashboard() {
	const mainDivStyle = useMainCustomStyle();
	const token = getAccessToken();
	const [selectedFilter, setSelectedFilter] = useState('All');
	const [feedbacks, setFeedbacks] = useState([]);
	const [feedbackData, setFeedbackData] = useState([]);
	const [feedbackMetrics, setFeedbackMetrics] = useState({});
	const [rowsPerPage, setRowsPerPage] = useState(5);
	const [page, setPage] = useState(0);

	useEffect(() => {
		getAllFeedbacks();
		getFeedbacksMetrics();
	}, []);

	async function getAllFeedbacks() {
		const data = await fetchAllFeedbacks(token);
		const filteredData = data.content?.sort(
			(a, b) => new Date(b.createdDate) - new Date(a.createdDate),
		);
		setFeedbacks(filteredData);
		setFeedbackData(filteredData);
	}

	async function getFeedbacksMetrics() {
		const data = await fetchFeedbackMetrics(token);
		const metric = data.content;
		setFeedbackMetrics({
			total: metric?.totalFeedback || 0,
			positive: metric?.positiveFeedback || 0,
			negative: metric?.negativeFeedback || 0,
			accuracy: metric?.accuracy || 0,
		});
	}

	function onSelectFilter(filter) {
		setSelectedFilter(filter);
		setPage(0);

		if (filter === 'All') {
			setFeedbacks(feedbackData);
		} else {
			const filterValue = filter === 'Positive' ? 'Yes' : 'No';
			const clone = feedbackData.filter(
				item => item.feedback === filterValue,
			);
			const filteredData = clone.sort(
				(a, b) => new Date(b.createdDate) - new Date(a.createdDate),
			);
			setFeedbacks(filteredData);
		}
	}

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = event => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};

	return (
		<>
			<SuperAdminSideNavigationBar />
			<div style={mainDivStyle}>
				<Grid container paddingLeft={'20px'} paddingRight={'20px'}>
					<Grid item paddingLeft="30px">
						<Typography
							variant="h6"
							fontWeight={'600'}
							color="#28696D">
							Feedback Dashboard
						</Typography>
					</Grid>
					<Grid container paddingLeft={'30px'} marginTop={'30px'}>
						<Grid
							container
							sx={{
								backgroundColor: '#FFFFFF',
								borderRadius: '12px',
								padding: '26px 16px 26px 16px',
							}}>
							<Grid
								container
								flexDirection={'row'}
								alignItems={'center'}>
								<MetricIcon />
								<Typography
									color={'#324054'}
									fontSize={'18px'}
									fontWeight={'500'}
									marginLeft={'15px'}>
									Metrics
								</Typography>
							</Grid>
							<Grid
								container
								columnGap={1}
								rowGap={1}
								sx={{
									flexDirection: 'row',
									paddingTop: '10px',
									paddingBottom: '10px',
									flexWrap: {
										xs: 'wrap',
										sm: 'nowrap',
									},
								}}>
								<MetricTile
									count={feedbackMetrics.total}
									name="Total Feedback Count"
									image={FeedbackCount}
								/>
								<MetricTile
									count={feedbackMetrics.positive}
									name="Positive Feedback Count"
									image={ThumpUp}
								/>
								<MetricTile
									count={feedbackMetrics.negative}
									name="Negative Feedback Count"
									image={ThumpDown}
								/>
								<MetricTile
									count={`${Math.round(feedbackMetrics.accuracy)}%`}
									name="Accuracy Rate"
									image={Accuracy}
								/>
							</Grid>
						</Grid>
						<Grid
							container
							sx={{
								backgroundColor: '#FFFFFF',
								borderRadius: '12px',
								padding: '26px 16px 26px 16px',
								marginTop: '8px',
							}}>
							<Grid
								container
								rowGap={2}
								sx={{
									display: 'flex',
									flexDirection: 'row',
									justifyContent: 'space-between',
									alignItems: 'center',
								}}>
								<Grid
									display={'flex'}
									flexDirection={'row'}
									alignItems={'center'}>
									<FeedbackTopicIcon />
									<Typography
										color={'#324054'}
										fontSize={'18px'}
										fontWeight={'500'}
										marginLeft={'15px'}>
										Feedback
									</Typography>
								</Grid>
								<Grid display={'flex'} columnGap={1}>
									{FILTER_TAGS.map((item, index) => {
										return (
											<FilterTag
												key={index}
												name={item}
												displayCount={false}
												isSelected={
													selectedFilter === item
												}
												onSelect={() =>
													onSelectFilter(item)
												}
											/>
										);
									})}
								</Grid>
							</Grid>
							<Grid container>
								<TableContainer
									component={Paper}
									style={{
										boxShadow: 'none',
										marginTop: '30px',
										border: '1px solid #d4dce4',
									}}>
									<Table
										sx={{
											minWidth: 700,
										}}
										aria-label="customized table">
										<TableHead>
											<TableRow>
												<StyledTableCell align="center">
													<Typography variant="tableHeader">
														User Account
													</Typography>
												</StyledTableCell>
												<StyledTableCell align="center">
													<Typography variant="tableHeader">
														Date
													</Typography>
												</StyledTableCell>
												<StyledTableCell align="center">
													<Typography variant="tableHeader">
														User Prompt
													</Typography>
												</StyledTableCell>
												<StyledTableCell align="center">
													<Typography variant="tableHeader">
														Enhanced Note
													</Typography>
												</StyledTableCell>
												<StyledTableCell align="center">
													<Typography variant="tableHeader">
														Feedback
													</Typography>
												</StyledTableCell>
												<StyledTableCell align="center">
													<Typography variant="tableHeader">
														Comment
													</Typography>
												</StyledTableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{feedbacks &&
											feedbacks.length > 0 ? (
												feedbacks
													.slice(
														page * rowsPerPage,
														page * rowsPerPage +
															rowsPerPage,
													)
													.map((item, index) => {
														return (
															<StyledTableRow
																key={index}>
																<StyledTableCell align="center">
																	<Typography variant="tableBody">
																		{
																			item.username
																		}
																	</Typography>
																</StyledTableCell>
																<StyledTableCell align="center">
																	<Typography variant="tableBody">
																		{new Date(
																			item.createdDate,
																		)
																			.getDate()
																			.toString()
																			.padStart(
																				2,
																				'0',
																			) +
																			'/' +
																			(
																				new Date(
																					item.createdDate,
																				).getMonth() +
																				1
																			)
																				.toString()
																				.padStart(
																					2,
																					'0',
																				) +
																			'/' +
																			new Date(
																				item.createdDate,
																			)
																				.getFullYear()
																				.toString()}
																	</Typography>
																</StyledTableCell>
																<StyledTableCell
																	align="center"
																	style={{
																		width: '20%',
																	}}>
																	<TextWithTooltip
																		text={
																			item.userNote
																		}
																	/>
																</StyledTableCell>
																<StyledTableCell
																	align="center"
																	style={{
																		width: '20%',
																	}}>
																	<Typography variant="tableBody">
																		<TextWithTooltip
																			text={
																				item.enhancedNote
																			}
																		/>
																	</Typography>
																</StyledTableCell>
																<StyledTableCell align="center">
																	<Typography variant="tableBody">
																		{
																			item.feedback
																		}
																	</Typography>
																</StyledTableCell>
																<StyledTableCell
																	align="center"
																	style={{
																		width: '20%',
																	}}>
																	<Typography variant="tableBody">
																		<TextWithTooltip
																			text={
																				item.comment
																			}
																		/>
																	</Typography>
																</StyledTableCell>
															</StyledTableRow>
														);
													})
											) : (
												<StyledTableRow>
													<StyledTableCell
														colSpan={6}
														align="center">
														<Typography variant="tableBody">
															No data available
														</Typography>
													</StyledTableCell>
												</StyledTableRow>
											)}
										</TableBody>
									</Table>
								</TableContainer>
								<TablePagination
									rowsPerPageOptions={[]}
									component="div"
									count={feedbacks ? feedbacks.length : 0}
									rowsPerPage={rowsPerPage}
									page={page}
									onPageChange={handleChangePage}
									onRowsPerPageChange={
										handleChangeRowsPerPage
									}
								/>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</div>
		</>
	);
}

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
	[`&.${tableCellClasses.head}`]: {
		backgroundColor: '#FFFFFF',
		color: 'rgba(0, 0, 0, 0.5)',
		paddingTop: '20px',
		fontSize: 12,
		fontWeight: 600,
	},
	[`&.${tableCellClasses.body}`]: {
		fontSize: 12,
		fontWeight: 400,
		color: '#000000',
		padding: '10px',
	},
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
	'&:nth-of-type(odd)': {
		background:
			'linear-gradient(0deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), #C2C9D1',
	},
	// hide last border
	'&:last-child td, &:last-child th': {
		border: 0,
	},
}));

export default FeedbackDashboard;
